import { useState, useEffect, useCallback } from "react";
import { IGif } from "services/types";

interface IUseFSNav {
  isFS: boolean;
  setActiveGif: any;
  gifs: IGif[];
  hasMore: boolean;
  fetchMore: () => void;
  ref: any;
  toggleFullscreen: (gif: IGif | null) => void;
  FSGif: IGif | null;
}

const getGifIndex = (id: string, gifs: IGif[]): number => {
  for (const idx in gifs) {
    if (gifs[idx].id === id) {
      return parseInt(idx);
    }
  }

  console.warn(`[PreviewFeed] Gif ${id} not found.`);

  return 0;
};

export const useFSNav = (props: IUseFSNav) => {
  const { isFS, setActiveGif, gifs, fetchMore, hasMore, ref, toggleFullscreen, FSGif } = props;
  const [activeSlide, setActiveSlide] = useState<number>(0);

  const getSlideOffsets = useCallback((isOpen: boolean) => {
    const currentRef = ref.current;

    if (isOpen) {
      const activeEl = currentRef.querySelector(`#gif_${FSGif?.id}`);

      return activeEl?.offsetTop || 0;
    }

    const offsets: number[] = [];
    const items = currentRef.querySelectorAll(".player");

    for (let i = 0; i < items.length; i++) {
      const em = items[i] as HTMLElement;
      offsets.push(em.offsetTop);
    }

    return offsets;
  }, [ref, FSGif]);

  const scrollFeed = useCallback((isOpen: boolean = false, idx: number) => {
    if (!ref.current) {
      return;
    }

    const offset = getSlideOffsets(isOpen);
    const ypos = isOpen ? offset : offset[idx];

    ref.current?.scrollTo({
      top: ypos,
      behavior: isOpen ? "auto" : "smooth",
    });
  }, [ref, getSlideOffsets]);

  const slideTo = useCallback((idx: number) => {
    if (idx < 0) {
      slideTo(0);
    }

    else if (idx >= gifs.length) {
      slideTo(gifs.length - 1);
    }

    else if (ref.current) {
      scrollFeed(false, idx);
      setActiveGif(gifs[idx].id);
      setActiveSlide(idx);

      // Last slide, load more if possible.
      if ((idx === gifs.length - 1) && hasMore) {
        fetchMore();
      }
    }
  }, [gifs, fetchMore, hasMore, scrollFeed, ref, setActiveGif]);

  const handleKeyDown = useCallback((e: KeyboardEvent): any => {
    if (e.key === "Escape") {
      e.preventDefault();
      toggleFullscreen(null);
    } else if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
      e.preventDefault();
      slideTo(activeSlide + 1);
    } else if (e.key === "ArrowUp" || e.key === "PageUp") {
      e.preventDefault();
      slideTo(activeSlide - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      slideTo(0);
    } else if (e.key === "End") {
      e.preventDefault();
      slideTo(gifs.length - 1);
    }
  }, [activeSlide, gifs.length, slideTo, toggleFullscreen]);

  // Install the keyboard handler.
  useEffect(() => {
    if (isFS) {
      document.addEventListener("keydown", handleKeyDown, false);

      return () => {
        document.removeEventListener("keydown", handleKeyDown, false);
      };
    }
  }, [isFS, handleKeyDown]);

  useEffect(() => {
    if (!isFS || !FSGif) {
      setActiveGif(undefined);
      setActiveSlide(0);

      return;
    }

    const slide = getGifIndex(FSGif.id, gifs);

    setActiveGif(FSGif.id);
    setActiveSlide(slide);
    scrollFeed(true, slide);
  }, [isFS, FSGif, gifs, scrollFeed, setActiveGif]);
};
